import { FC, ReactElement, useState } from 'react'

import { Icon } from '../../components'
import {
    EventLog,
    EventLogActionType,
    EventLogDataItem,
    EventLogDataType,
    EventLogEntityTypeResource,
    EventLogMailNotification,
    userEntityTypes,
} from '../../entities/EventLog/EventLog'
import { getEventLogEntityLink, getEventLogEntityTranslation } from '../../entities/EventLog/EventLogHelpers'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { useTypedDispatch } from '../../redux/store'
import { DateFormat } from '../../types/dateFormatTypes'
import { PzLogDataTable } from './subcomponents'
import PzLogListItem from './subcomponents/PzLogListItem/PzLogListItem'

import './PzLogDetail.scss'

interface PzLogDetailProps {
    log: EventLog;
    categoryName?: string;
    className?: string;
}

/**
 * log.items contains the data to display in the data table, except when the action is notification.
 * If so, we extract the recipients from the mail notification data and add them to the data table.
 */
const generateDataTableItems = (log: EventLog): EventLogDataItem[] => {
    if (log.action !== EventLogActionType.notification) return log.items

    const notification = log.items[0]?.data[EventLogDataType.mailNotification] as EventLogMailNotification | undefined

    if (! notification) return log.items

    return [{
        id: '',
        type: '',
        data: {
            [EventLogDataType.mailNotification]: notification.recipients,
        },
    }]
}

const generateLogCaption = (log: EventLog): string | ReactElement | null => {
    if (log.action !== EventLogActionType.notification) return null

    const notification = log.items[0]?.data[EventLogDataType.mailNotification] as EventLogMailNotification | undefined

    return (
        <>
            <h3 className="pz-log-detail__caption__heading">{trans('common.subject')}</h3>
            <p>{notification?.subject || '…'}</p>
            <h3 className="pz-log-detail__caption__heading">{trans('common.message')}</h3>
            <p>{notification?.body || '…'}</p>
        </>
    )
}

const PzLogDetail: FC<PzLogDetailProps> = ({
    log,
    categoryName,
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch()

    const [isNavigating, setIsNavigating] = useState(false)

    const handleNavigateToEntity = async (): Promise<void> => {
        if (isNavigating) return

        setIsNavigating(true)

        try {
            const route = await getEventLogEntityLink(log, dispatch)

            if (route) {
                window.open(route)
            }
        } finally {
            setIsNavigating(false)
        }
    }

    const showLinkToEntity = log.action !== EventLogActionType.deleted && (
        log.type === EventLogEntityTypeResource.shift
        || log.type === EventLogEntityTypeResource.track
        || log.type === EventLogEntityTypeResource.department
        || userEntityTypes.includes(log.type))

    const caption = generateLogCaption(log)

    const entityTranslation = getEventLogEntityTranslation(log.type)

    const createdAt = formatDate(log.createdAt, DateFormat.dateTime)

    const showName = userEntityTypes.includes(log.type)

    return (
        <div className={`pz-log-detail ${className}`}>
            <ul className="pz-log-detail__property-list">
                {showName && (
                    <button
                        type="button"
                        onClick={handleNavigateToEntity}
                        disabled={isNavigating}
                        className="pz-log-detail__list-item-link"
                        title={trans('common.linkOpensInNewTab')}
                    >
                        <PzLogListItem
                            label={trans('common.name')}
                            value={log.name}
                        />
                    </button>
                )}

                <PzLogListItem
                    label={trans('compositions.pzLogDetail.changeBy')}
                    value={log.createdBy}
                />

                <PzLogListItem
                    label={trans('common.category')}
                    value={categoryName || ''}
                />

                <PzLogListItem
                    label={trans('compositions.pzLogDetail.dateAndTime')}
                    value={createdAt}
                />
            </ul>

            <div className="pz-log-detail__content">
                {caption && (
                    <div className="pz-log-detail__caption">
                        {trans('compositions.pzLogDetail.caption')}
                        {caption}
                    </div>
                )}

                <PzLogDataTable dataItems={generateDataTableItems(log)} />

                <div className="pz-log-detail__buttons-wrapper">
                    {showLinkToEntity && (
                        <button
                            type="button"
                            onClick={handleNavigateToEntity}
                            disabled={isNavigating}
                            className="pz-log-detail__view-button"
                            title={trans('common.linkOpensInNewTab')}
                        >
                            {trans('compositions.pzLogDetail.viewEntity', { entity: entityTranslation.toLowerCase() })}

                            <Icon
                                name="chevron-right"
                                className="pz-log-detail__view-button-icon"
                            />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PzLogDetail
